<template>
  <div class="choose-team-card" :class="{ 'enlarge': enlarge, 'companies-mode': hasSeveralCompanies }">
    <div
      class="cache"
      :class="{ visible: enlarge }"
      @click="focusOut"
    />
    <el-card class="card">
      <div class="content">
        <div id="team-list" class="teams">
          <div v-if="!hasSeveralCompanies" class="header">
            <div>
              <p v-if="isHeader" class="title">{{ $t('team.select_team.header.title') }}</p>
              <p v-else class="title" >{{ $t('team.select_team.title') }}</p>
            </div>
            <div
              v-if="!isMobile"
              class="toggle"
              @click="toggleEnlarge"
            >
              <img
                v-if="!enlarge"
                src="/static/img/icones/icon-enlarge.svg"
                alt="ask"
                class="enlarge-icon"
              >
              <img
                v-else
                src="/static/img/icones/quit.svg"
                alt="close"
                class="quit"
              >
            </div>

          </div>

          <div class="teams-scrollbar" :class="{ 'company-columns': hasSeveralCompanies }">
            <div v-if="hasSeveralCompanies" class="company-list">
              <p class="company-list-title">{{ $t('header.account_list.companies.label') }}</p>
              <div
                v-for="company in accountStore.companies"
                :key="`company-${company.id}`"
                class="company-item"
                :class="{ active: company.id === displayedRootId }"
                @click="previewRootId = company.id"
              >
                <div class="company-logo" :style="{ 'background-image': `url(${companyLogo(company)})` }" />
                <span class="company-name">{{ company.name }}</span>
              </div>
            </div>
            <div class="company-accounts">
              <p v-if="hasSeveralCompanies" class="company-accounts-title">
                {{ $t('header.account_list.company_accounts.label', { name: displayedCompany.name }) }}
              </p>
              <div
                v-if="showAllTeams"
                class="all-teams"
                @click="selectCompany('all')"
              >
                <div class="title-flex">
                  <div class="title-and-rond">
                    <div class="rond" :class="{ 'on': isCurrentDisplayed && !companyStore.childrenActive }">
                      <div class="loading">
                        <div class="loader"/>
                      </div>
                    </div>
                    <div class="title-container">
                      <div class="title">{{ allTeamsLabel || $t('header.account_list.all_accounts.label') }}</div>
                    </div>
                    <div v-if="selectParents && parentCount" class="pending-badge">
                      {{ parentCount }}
                    </div>
                  </div>
                </div>
              </div>
              <RecursiveTeamList
                :key="`tree-${displayedRootId}`"
                :has-to-select-leaf="!isHeader && !selectParents"
                :parent-name="displayedCompany ? displayedCompany.name : ''"
                :counter="counter"
                :children="displayedChildren"
                @company-selected="selectCompany"
              />
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useAccountStore } from '@/stores/account';
import { useCompanyStore } from '@/stores/company';
import { useCustomerKpiStore } from '@/stores/customerKpi';
import RecursiveTeamList from '../../pages/checkout/chooseTeam/recursiveTeamList';

export default {
  name: 'ChooseTeamCard',
  components: { RecursiveTeamList },
  props: {
    showAllTeams: {
      type: Boolean,
      default: false,
    },
    isHeader: {
      type: Boolean,
      default: false,
    },
    allTeamsLabel: {
      type: String,
      default: '',
    },
    selectParents: {
      type: Boolean,
      default: false,
    },
    counter: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      enlarge: false,
      previewRootId: null,
      isMobile: /Mobi/i.test(window.navigator.userAgent),
    };
  },
  computed: {
    ...mapStores(useAccountStore, useCompanyStore, useCustomerKpiStore),
    parentCount() {
      return this.accountStore.company ? this.customerKpiStore.accountCount(this.accountStore.company.id, this.counter) : 0;
    },
    childrenCompany() {
      if (this.accountStore.company) {
        return this.accountStore.company.children;
      }
      return false;
    },
    hasSeveralCompanies() {
      return this.isHeader && this.accountStore.companies.length > 1;
    },
    currentRootId() {
      return this.accountStore.company ? this.accountStore.company.id : null;
    },
    displayedRootId() {
      return this.previewRootId || this.currentRootId;
    },
    isCurrentDisplayed() {
      return this.displayedRootId === this.currentRootId;
    },
    displayedCompany() {
      return this.isCurrentDisplayed
        ? this.accountStore.company
        : this.accountStore.companies.find((company) => company.id === this.displayedRootId);
    },
    displayedChildren() {
      return this.isCurrentDisplayed ? this.childrenCompany : this.displayedCompany.children;
    },
  },
  mounted() {
  },
  methods: {
    toggleEnlarge() {
      this.enlarge = !this.enlarge;
    },
    focusOut() {
      this.enlarge = false;
    },
    resetPreview() {
      this.previewRootId = null;
    },
    // An account of another company of the login is a switch, not a filter.
    selectCompany(company) {
      if (this.isCurrentDisplayed) {
        this.$emit('selected-company', company);
      } else {
        this.$emit('selected-account', company === 'all' ? this.displayedCompany : company);
      }
    },
    companyLogo(company) {
      return company.logo && company.logo.format ? company.logo.format.small : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.choose-team-card.companies-mode {
  .card,
  :deep(.el-card__body) {
    padding: 0;
  }

  .teams-scrollbar {
    height: 100%;
    margin: 0;
    overflow: hidden;
  }
}

.company-columns {
  display: flex;

  @media (max-width: 767px) {
    flex-direction: column;
  }
}

.company-list {
  flex: 0 0 250px;
  padding: 18px 10px;
  overflow-y: auto;
  background-color: #f2f3f8;

  .company-list-title {
    margin: 0 0 6px;
    padding: 0 10px;
    color: #4b4f6b;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
}

.company-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 10px;
  border-radius: 5px;
  color: #1c184a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(.active) {
    background-color: #e6e8f2;
  }

  &.active {
    background-color: #1c184a;
    color: #fff;
    cursor: default;
  }

  .company-logo {
    flex: 0 0 24px;
    height: 24px;
    border-radius: 12px;
    background-color: #fff;
    background-position: center;
    background-size: cover;
  }

  .company-name {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

.company-columns .company-accounts {
  padding: 18px;
  overflow-y: auto;
}

.company-accounts {
  flex: 1;
  min-width: 0;

  .company-accounts-title {
    margin: 0;
    margin-bottom: 15px;
    color: #1c184a;
    font-size: 16px;
    font-weight: bold;
  }
}
</style>
